import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API, getImageUrl } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Activity, Users, TrendingUp, TrendingDown, AlertTriangle, Download,
  FileText, FileSpreadsheet, Sparkles, ChevronDown, RefreshCw, Loader2,
  BarChart3, Building2, PieChart
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import PlanBanner from '../../components/PlanBanner';
import { registerPushSubscription } from '../../components/NotificationBell';

const DashboardOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [attendanceChart, setAttendanceChart] = useState([]);
  const [evolutionChart, setEvolutionChart] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [gyms, setGyms] = useState([]);
  const [companyPlan, setCompanyPlan] = useState(null);

  const CHART_COLORS = ['#2563EB', '#F97316', '#10B981', '#64748B', '#8B5CF6'];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboardRes, attendanceRes, evolutionRes, gymsRes, planRes] = await Promise.all([
        axios.get(`${API}/rh/dashboard`),
        axios.get(`${API}/rh/attendance-chart?days=30`),
        axios.get(`${API}/rh/evolution-chart`),
        axios.get(`${API}/gyms`),
        axios.get(`${API}/companies/plan`).catch(() => ({ data: {} }))
      ]);
      
      setDashboard(dashboardRes.data);
      setAttendanceChart(attendanceRes.data);
      setEvolutionChart(evolutionRes.data);
      setGyms(gymsRes.data);
      setCompanyPlan(planRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    registerPushSubscription();
  }, [fetchData]);

  const fetchInsights = async () => {
    setInsightsLoading(true);
    try {
      const res = await axios.get(`${API}/rh/ai-insights`);
      setInsights(res.data);
    } catch (error) {
      console.error('Error fetching insights:', error);
      toast.error('Erro ao gerar insights');
    } finally {
      setInsightsLoading(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const res = await axios.get(`${API}/rh/export/pdf`, {
        
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'relatorio_saude.txt');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Relatório exportado!');
    } catch (error) {
      toast.error('Erro ao exportar');
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`${API}/rh/export/excel`, {
        
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'relatorio_colaboradores.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Relatório exportado!');
    } catch (error) {
      toast.error('Erro ao exportar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const evolutionData = dashboard?.evolution_distribution ? [
    { name: 'Alta', value: dashboard.evolution_distribution.high, color: '#10B981' },
    { name: 'Moderada', value: dashboard.evolution_distribution.moderate, color: '#2563EB' },
    { name: 'Estável', value: dashboard.evolution_distribution.stable, color: '#64748B' },
    { name: 'Regressão', value: dashboard.evolution_distribution.regression, color: '#EF4444' }
  ] : [];

  return (
    <div className="space-y-6">
      {/* Plan Banner */}
      <PlanBanner planData={companyPlan} variant="company" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Dashboard de Saúde
          </h1>
          <p className="text-slate-600 mt-1">Visão geral da saúde corporativa</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={fetchData}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="export-dropdown">
                <Download className="w-4 h-4" />
                Exportar
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportPDF} data-testid="export-pdf-btn">
                <FileText className="w-4 h-4 mr-2" />
                Exportar PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportExcel} data-testid="export-excel-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Exportar Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Metrics Grid - Bento Layout */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Health Score - Hero Widget */}
        <Card className="col-span-12 md:col-span-8 lg:col-span-8 row-span-2 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white border-0 overflow-hidden">
          <CardContent className="p-8 h-full flex flex-col justify-between">
            <div>
              <p className="text-slate-300 text-sm font-medium mb-2">Score de Saúde Corporativa</p>
              <div className="flex items-end gap-4 mb-4">
                <span className="text-6xl md:text-7xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {dashboard?.health_score || 0}
                </span>
                <span className="text-2xl text-slate-400 mb-3">/100</span>
              </div>
              <Progress 
                value={dashboard?.health_score || 0} 
                className="h-3 bg-slate-700"
              />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-8">
              <div>
                <p className="text-slate-400 text-xs mb-1">Colaboradores</p>
                <p className="text-2xl font-bold">{dashboard?.total_students || 0}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs mb-1">Frequência</p>
                <p className="text-2xl font-bold">{dashboard?.attendance_rate || 0}%</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs mb-1">Check-ins (30d)</p>
                <p className="text-2xl font-bold">{dashboard?.total_checkins_30d || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* IMC Card */}
        <Card className="col-span-12 md:col-span-4 lg:col-span-4 card-hover border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-500 text-sm font-medium">IMC Médio</span>
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {dashboard?.avg_imc || 0}
            </p>
            <p className="text-slate-500 text-sm mt-1">
              {dashboard?.avg_imc < 25 ? 'Dentro do ideal' : 'Acima do ideal'}
            </p>
          </CardContent>
        </Card>

        {/* Body Fat Card */}
        <Card className="col-span-12 md:col-span-4 lg:col-span-4 card-hover border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-500 text-sm font-medium">Gordura Corporal</span>
              <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                <PieChart className="w-5 h-5 text-orange-500" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {dashboard?.avg_body_fat || 0}%
            </p>
            <p className="text-slate-500 text-sm mt-1">Média dos colaboradores</p>
          </CardContent>
        </Card>

        {/* Lean Mass Card */}
        <Card className="col-span-12 md:col-span-4 lg:col-span-4 card-hover border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-500 text-sm font-medium">Massa Magra</span>
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
            </div>
            <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {dashboard?.avg_lean_mass || 0} kg
            </p>
            <p className="text-slate-500 text-sm mt-1">Média dos colaboradores</p>
          </CardContent>
        </Card>

        {/* Attendance Chart */}
        <Card className="col-span-12 lg:col-span-8 card-hover border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Frequência (últimos 30 dias)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={attendanceChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickFormatter={(value) => new Date(value).getDate()}
                  />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 8px 30px rgba(15,23,42,0.12)'
                    }}
                    labelFormatter={(value) => new Date(value).toLocaleDateString('pt-BR')}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="checkins" 
                    stroke="#2563EB" 
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6, fill: '#2563EB' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Evolution Distribution */}
        <Card className="col-span-12 lg:col-span-4 card-hover border-slate-100">
          <CardHeader>
            <CardTitle className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Distribuição de Evolução
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={evolutionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {evolutionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 8px 30px rgba(15,23,42,0.12)'
                    }}
                  />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* AI Insights */}
        <Card className="col-span-12 card-hover border-slate-100">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Sparkles className="w-5 h-5 text-blue-600" />
              Insights de IA
            </CardTitle>
            <Button 
              onClick={fetchInsights}
              disabled={insightsLoading}
              data-testid="generate-insights-btn"
              className="bg-slate-900 hover:bg-slate-800"
            >
              {insightsLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Gerar Insights
            </Button>
          </CardHeader>
          <CardContent>
            {insights ? (
              <div className="bg-slate-50 rounded-xl p-6">
                <div className="prose prose-slate max-w-none whitespace-pre-wrap text-sm">
                  {insights.insights}
                </div>
                <p className="text-xs text-slate-400 mt-4">
                  Gerado em: {new Date(insights.generated_at).toLocaleString('pt-BR')}
                </p>
              </div>
            ) : (
              <div className="bg-slate-50 rounded-xl p-8 text-center">
                <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  Clique em &quot;Gerar Insights&quot; para obter análises inteligentes sobre a saúde corporativa
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Partner Gyms */}
        {gyms.length > 0 && (
          <Card className="col-span-12 card-hover border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <Building2 className="w-5 h-5 text-blue-600" />
                Academias Parceiras
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {gyms.map((gym) => (
                  <div key={gym.gym_id} className="bg-slate-50 rounded-xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Activity className="w-5 h-5 text-orange-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{gym.name}</p>
                      {gym.address && <p className="text-xs text-slate-500 truncate">{gym.address}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Low Frequency Alerts */}
        {dashboard?.low_frequency_alerts?.length > 0 && (
          <Card className="col-span-12 border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2 text-orange-700" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <AlertTriangle className="w-5 h-5" />
                Alertas de Baixa Frequência
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dashboard.low_frequency_alerts.map((alert, index) => (
                  <div 
                    key={alert.student_id || alert.email || alert.name || index}
                    className="bg-white rounded-xl p-4 border border-orange-200"
                  >
                    <p className="font-medium text-slate-900">{alert.name}</p>
                    <p className="text-sm text-orange-600">
                      {alert.checkins_count} check-ins nos últimos 30 dias
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};


export default DashboardOverview;
